// ── Dropdown entity shapes ──────────────────────────────────────

import { AssistantCreateApi, AssistantOptionsApi } from '../Services/AgentApiProviderTypes';

export interface IdNameEntity {
  id: string;
  name: string;
}

export interface KeyNameEntity {
  key: string;
  name: string;
}

export interface DictionaryEntity {
  id: string;
  title: string;
}

export interface PromptEntity extends IdNameEntity {
  pattern_type?: string;
}

// ── UI-only value types (for options in dropdowns) ──────────────

export type ModelName =
  | 'gpt-4.1-mini'
  | 'gpt-4.1'
  | 'gpt-5-mini'
  | 'gpt-5'
  | 'gpt-5.1'
  | 'gpt-5.2'
  | 'gpt-5.4';

// ── Client-side form state ──────────────────────────────────────

export interface AgentConfig {
  name: string;
  isActive: boolean;
  agentType: string;
  modelName: string | null;
  reasoning: string | null;
  temperature: number;
  detailsLevel: string | undefined;
  promptId: string | undefined;
  selectedVoice: string;
  memoryType: string | null;
  memoryLimit: number | null;
  skills: string[];
  frameIds: string[];
  markerIds: string[];
  swarmKeys: string[];
  notificationIds: string[];
  documentCollectionIds: string[];
  informationPackageCollectionIds: string[];
  connectionIds: string[];
  catalog: string | null | undefined;
  dictionaryId: string | null;
  templateIds: string[] | null;
  auditExecution: boolean;
}

// ── Reference data for dropdowns ────────────────────────────────

export interface AgentReferenceData {
  prompts: PromptEntity[];
  frames: IdNameEntity[];
  markers: IdNameEntity[];
  notifications: IdNameEntity[];
  documentCollections: IdNameEntity[];
  informationPackageCollections: IdNameEntity[];
  connections: IdNameEntity[];
  catalogs: IdNameEntity[];
  dictionaries: DictionaryEntity[];
  swarms: KeyNameEntity[];
  templates: IdNameEntity[];
  options: AssistantOptionsApi | null;
}

// ── Feature flags / permissions ─────────────────────────────────

export interface AgentPermissions {
  isDisabled: boolean;
  isOrganizationsPermission: boolean;
  isDocumentPermission: boolean;
  isPromptRealtime: boolean;
  disableCollections: boolean;
  collectionTextWarning: string;
}

// ── Pure helpers ────────────────────────────────────────────────

export function getCollectionDisableReason(
  agentType: string | undefined,
  skills: string[],
  reasoning: string | null,
): string | null {
  if (agentType !== 'RAG') return null;
  if (skills?.some((s) => s === 'SKILL:WEB_SEARCH' || s === 'SKILL:WEB_SEARCH_HIGH')) {
    return 'Web search does not support Collections';
  }
  if (reasoning === 'minimal') {
    return 'Minimal reasoning does not support Collections';
  }
  return null;
}

export function configToPayload(c: AgentConfig): AssistantCreateApi {
  return {
    name: c.name,
    prompt_id: c.promptId,
    voice: c.selectedVoice,
    details_level: c.detailsLevel,
    temperature: c.temperature,
    notification_keys: c.notificationIds,
    document_collection_keys: c.documentCollectionIds,
    information_package_collection_keys: c.informationPackageCollectionIds,
    connections: c.connectionIds,
    frame_keys: c.frameIds,
    marker_keys: c.markerIds,
    is_active: c.isActive,
    assistant_type: c.agentType,
    memory_type: c.memoryType,
    memory_limit: c.memoryLimit,
    catalog_key: c.catalog,
    taxonomy_dictionary_id: c.dictionaryId,
    skills: c.skills,
    model_name: c.modelName,
    reasoning: c.reasoning,
    swarm: c.swarmKeys,
    templates: c.templateIds,
    audit_execution: c.auditExecution,
  };
}

// ── Defaults ────────────────────────────────────────────────────

export const INITIAL_CONFIG: AgentConfig = {
  name: '',
  isActive: false,
  agentType: undefined as unknown as string,
  modelName: null,
  reasoning: null,
  temperature: 30,
  detailsLevel: undefined,
  promptId: undefined,
  selectedVoice: 'cedar',
  memoryType: null,
  memoryLimit: null,
  skills: [],
  frameIds: [],
  markerIds: [],
  swarmKeys: [],
  notificationIds: [],
  documentCollectionIds: [],
  informationPackageCollectionIds: [],
  connectionIds: [],
  catalog: undefined,
  dictionaryId: null,
  templateIds: null,
  auditExecution: true,
};

export const EMPTY_DATA: AgentReferenceData = {
  prompts: [],
  frames: [],
  markers: [],
  notifications: [],
  documentCollections: [],
  informationPackageCollections: [],
  connections: [],
  catalogs: [],
  dictionaries: [],
  swarms: [],
  templates: [],
  options: null,
};
